import { useEffect, useRef, useState } from 'react';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetRole from '../../../API/hooks/useGetRole';
import useGetStreamConfig from '../../../API/hooks/videos/useGetStreamConfig';
import S from './LiveVideoStyles';
import { useStreamTimer, formatDuration } from './useStreamTimer';

// ---------- types ----------

export type HlsPlayerProps = {
  deviceId: string;
  cameraId: number;
  deviceStatus: boolean;
  streamVisibilityDuration: number;
};

type HlsVideoProps = {
  playbackUrl: string;
  streamVisibilityDuration: number;
  onChannelGone: () => void;
  onReset: () => void;
  onDurationEnded: () => void;
  onFirstPlay: () => void;
};

// ---------- constants ----------

const STREAM_START_TIMEOUT_MS = 60_000;
const STREAM_RETRY_INTERVAL_MS = 3_000;

// ---------- hls.js CDN global types ----------

// window.Hls is injected by the CDN script in index.html. This is the
// Firefox fallback path for IVS playback: the IVS Player SDK's own WASM
// worker has a confirmed, undocumented bug where it keeps only a ~20-70ms
// buffer ahead of the playhead on Firefox specifically (Gecko MSE interacts
// badly with it — see Mozilla bugs 1520894/1520891), causing continuous
// rebuffering regardless of setLiveLowLatencyEnabled/setInitialBufferDuration.
// hls.js manages its own MSE buffering independently of IVS's WASM worker and
// was verified empirically to play the same IVS channel smoothly on Firefox.
// AWS does not officially support third-party players against IVS, but the
// manifest is plain HLS/CMAF with no DRM, so this works in practice.
declare global {
  interface Window {
    Hls: HlsConstructor;
  }
}

interface HlsLevel {
  height: number;
  bitrate: number;
}

interface HlsErrorData {
  fatal: boolean;
  details: string;
}

interface HlsInstance {
  loadSource: (url: string) => void;
  attachMedia: (video: HTMLVideoElement) => void;
  destroy: () => void;
  startLoad: () => void;
  levels: HlsLevel[];
  currentLevel: number;
  on: (event: string, cb: (event: string, data: any) => void) => void;
}

interface HlsConstructor {
  isSupported: () => boolean;
  Events: {
    ERROR: string;
    MANIFEST_PARSED: string;
    LEVEL_SWITCHED: string;
  };
  ErrorTypes: {
    NETWORK_ERROR: string;
  };
  ErrorDetails: {
    MANIFEST_LOAD_ERROR: string;
  };
  new (config?: Record<string, unknown>): HlsInstance;
}

// ---------- video player ----------

// The <video> element must stay in the DOM at all times — hls.js attaches to
// it once on mount. Visibility is toggled via CSS so the player always has a
// valid element to write frames to. Mirrors IvsPlayer.tsx's IvsVideo
// structure/behavior (retry loop, timer wiring, status overlays, quality
// picker) so switching between the two players is transparent to the rest
// of the app.
function HlsVideo({
  playbackUrl, streamVisibilityDuration, onChannelGone, onReset, onDurationEnded, onFirstPlay,
}: Readonly<HlsVideoProps>): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { canPlayVideoAudio } = useGetRole();
  const [status, setStatus] = useState<'waiting' | 'buffering' | 'playing' | 'ended' | 'error'>('waiting');
  const [everPlayed, setEverPlayed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [qualities, setQualities] = useState<HlsLevel[]>([]);
  const [activeQualityIndex, setActiveQualityIndex] = useState(-1);
  const playerRef = useRef<HlsInstance | null>(null);
  const durationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamVisibilityDurationRef = useRef(streamVisibilityDuration);
  useEffect(() => { streamVisibilityDurationRef.current = streamVisibilityDuration; }, [streamVisibilityDuration]);

  useEffect(() => {
    const { Hls } = window;

    if (!Hls?.isSupported()) {
      setStatus('error');
      setErrorMessage('HLS playback is not supported in this browser.');
      return () => {};
    }
    if (!videoRef.current) return () => {};

    const video = videoRef.current;
    const player = new Hls();
    playerRef.current = player;
    player.attachMedia(video);

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let totalWaited = 0;

    const tryLoad = (): void => {
      player.loadSource(playbackUrl);
    };

    const scheduleRetry = (): void => {
      totalWaited += STREAM_RETRY_INTERVAL_MS;
      if (totalWaited >= STREAM_START_TIMEOUT_MS) {
        setStatus('error');
        setErrorMessage('Device did not start streaming in time. Please try again.');
        return;
      }
      retryTimer = setTimeout(tryLoad, STREAM_RETRY_INTERVAL_MS);
    };

    const onWaiting = (): void => setStatus('buffering');

    const onPlaying = (): void => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (!durationTimeoutRef.current) {
        onFirstPlay();
        durationTimeoutRef.current = setTimeout(() => {
          onDurationEnded();
        }, streamVisibilityDurationRef.current);
      }
      setEverPlayed(true);
      setStatus('playing');
    };

    const onEnded = (): void => {
      setStatus('ended');
      onChannelGone();
    };

    // play() can be silently rejected by the browser's autoplay policy when
    // audio isn't muted and this call isn't a direct result of a user
    // gesture — same caveat as IvsPlayer.tsx's tryLoad().
    const playVideo = (): void => {
      try {
        const playResult = video.play() as unknown;
        if (playResult instanceof Promise) {
          playResult.catch((err: unknown) => {
            console.warn('[HlsPlayer] play() was rejected (likely autoplay policy):', err);
          });
        }
      } catch (err) {
        console.warn('[HlsPlayer] play() threw:', err);
      }
    };

    const onManifestParsed = (): void => {
      setQualities(player.levels);
      setActiveQualityIndex(player.currentLevel);
      playVideo();
    };

    const onLevelSwitched = (_event: string, data: { level: number }): void => {
      setActiveQualityIndex(data.level);
    };

    const onHlsError = (_event: string, data: HlsErrorData): void => {
      console.error('[HlsPlayer] error:', data);
      if (!data.fatal) return;

      // manifestLoadError (404): stream not live yet — keep retrying silently,
      // same as IvsPlayer.tsx's NOT_AVAILABLE/404 handling.
      if (data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR) {
        scheduleRetry();
        return;
      }

      // Any other fatal error (network/media/other) — surface it and reset
      // the parent to the start screen.
      setStatus('error');
      setErrorMessage('Stream unavailable. Please try again.');
      onChannelGone();
    };

    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('ended', onEnded);
    player.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
    player.on(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
    player.on(Hls.Events.ERROR, onHlsError);

    tryLoad();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (durationTimeoutRef.current) {
        clearTimeout(durationTimeoutRef.current);
        durationTimeoutRef.current = null;
      }
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('ended', onEnded);
      player.destroy();
      playerRef.current = null;
    };
  }, [playbackUrl]);

  useEffect(() => {
    if (!durationTimeoutRef.current) return;
    clearTimeout(durationTimeoutRef.current);
    durationTimeoutRef.current = setTimeout(() => {
      onDurationEnded();
    }, streamVisibilityDuration);
  }, [streamVisibilityDuration]);

  const handleQualityChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const index = Number(e.target.value);
    if (playerRef.current) {
      playerRef.current.currentLevel = index;
      setActiveQualityIndex(index);
    }
  };

  return (
    <div className="video-stage">
      <video
        className="live-video"
        ref={videoRef}
        playsInline
        autoPlay
        muted={!canPlayVideoAudio}
        style={{ visibility: everPlayed ? 'visible' : 'hidden' }}
      >
        <track kind="captions" src="" label="English" />
      </video>

      {!everPlayed && status === 'waiting' && (
        <div className="video-overlay">
          <span>Waiting for device to start streaming...</span>
        </div>
      )}

      {status === 'buffering' && (
        <div className="video-overlay buffering-overlay">
          <span>Buffering...</span>
        </div>
      )}

      {(status === 'error' || status === 'ended') && (
        <div className="video-overlay">
          <span>{status === 'ended' ? 'Stream ended.' : errorMessage}</span>
          <button type="button" onClick={onReset}>Start new stream</button>
        </div>
      )}

      {qualities.length > 1 && status === 'playing' && (
        <div className="quality-bar">
          <select value={activeQualityIndex} onChange={handleQualityChange}>
            {qualities.map((q, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <option key={index} value={index}>
                {q.height}p
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ---------- HLS player shell (fetch config → show video) ----------

// Fetches the same /credentials endpoint as IvsPlayer/WebRtcPlayer. Used as a
// Firefox-only fallback for the srt/rtmp → IVS playback path — see
// StreamView in LiveVideo.tsx for the browser check that picks this over
// IvsPlayer.
function HlsPlayer({
  deviceId, cameraId, deviceStatus, streamVisibilityDuration,
}: Readonly<HlsPlayerProps>): JSX.Element {
  const fleetId = useGetFleetId();
  const [started, setStarted] = useState(false);
  const [durationEnded, setDurationEnded] = useState(false);
  const { data: streamConfig, isLoading } = useGetStreamConfig(deviceId, fleetId, cameraId, false, started);
  const { state: timerState, startTimer, stopTimer } = useStreamTimer(streamVisibilityDuration);

  const shellOverlay = (message: string, action?: JSX.Element): JSX.Element => (
    <S.VideoContainer>
      <div className="video-stage-placeholder">
        <span>{message}</span>
        {action}
      </div>
    </S.VideoContainer>
  );

  if (deviceStatus === false) {
    return shellOverlay('Device is offline. Please try again when device is back online.');
  }

  if (!started) {
    return shellOverlay(
      'Press "Start" to request live video',
      <button type="button" onClick={() => setStarted(true)}>Start stream</button>,
    );
  }

  if (isLoading) {
    return shellOverlay('Loading stream configuration...');
  }

  if (!streamConfig?.playbackUrl) {
    return shellOverlay('Waiting for stream configuration...');
  }

  if (durationEnded) {
    return shellOverlay(
      'Stream duration limit reached. Please try again or change stream duration.',
      <button type="button" onClick={() => { setDurationEnded(false); setStarted(false); }}>Start new stream</button>,
    );
  }

  return (
    <S.VideoContainer>
      <HlsVideo
        playbackUrl={streamConfig.playbackUrl}
        streamVisibilityDuration={streamVisibilityDuration}
        onChannelGone={() => { stopTimer(); setStarted(false); }}
        onReset={() => { stopTimer(); setStarted(false); }}
        onDurationEnded={() => { stopTimer(); setDurationEnded(true); }}
        onFirstPlay={startTimer}
      />
      {timerState.elapsed > 0 && (
        <div className="stream-timer">
          <span>Stream duration: {formatDuration(timerState.elapsed)}</span>
          {timerState.remaining !== null && (
            <span>Remaining: {formatDuration(timerState.remaining)}</span>
          )}
        </div>
      )}
    </S.VideoContainer>
  );
}

export default HlsPlayer;
